'use client';

import { fromPhpLangId } from '@ifixit/i18n/php-langid';
import {
   type Country,
   type CountryCode,
   type UserLocaleCode,
   countryByCode,
   parseUserLocale,
} from '@ifixit/i18n/user-locale';
import { isEUCountryCode } from '@ifixit/utils';
import { useCookieState } from './use-cookie-state';
import { useMemo } from 'react';

/**
 * Detects the user's locale from geo headers (via middleware cookie) and
 * browser language preferences. Returns null when the country is unknown.
 */
export function useDetectedUserLocale(): UserLocaleCode | null {
   const [countryCode] = useCookieState('detected-country');

   return useMemo(() => userLocaleFromCountryCode(countryCode), [countryCode]);
}

/**
 * Resolve a geo-detected country code to the best-matching user locale.
 *
 * `preferredLanguages` is consulted in order to pick a locale for
 * multi-locale countries (e.g. `de`/`fr`/`it`/`en` for Switzerland); it
 * defaults to the browser's language preferences. Falls back to the country's
 * first locale, and returns null when the country is unknown.
 */
export function userLocaleFromCountryCode(
   countryCode: string | null | undefined,
   preferredLanguages: readonly string[] = browserLanguages()
): UserLocaleCode | null {
   const normalizedCountryCode = normalizeCountryCode(countryCode);
   if (!normalizedCountryCode) return null;
   const country = countryByCode[normalizedCountryCode as CountryCode];
   if (!country) return null;
   return matchLanguage(country, preferredLanguages);
}

/**
 * Resolve a geo-detected country code to the user locale signal for a Carpenter
 * page, preferring the page's language. When the geo country has no locale in
 * the page language but belongs to the EU store region, English visitors share
 * the pan-European store (en-EU).
 */
export function geoUserLocaleForPhpLangId(
   countryCode: string | null | undefined,
   phpLangId: string
): UserLocaleCode | null {
   const language = fromPhpLangId(phpLangId);
   const userLocale = userLocaleFromCountryCode(countryCode, [language]);
   if (userLocale == null) return null;
   if (parseUserLocale(userLocale).language === language) return userLocale;
   if (countryCode != null && isEUCountryCode(countryCode)) return 'en-EU';
   return userLocale;
}

/**
 * Match preferred languages against a country's available locales. Falls back
 * to the country's first locale if no match is found.
 */
function matchLanguage(country: Country, preferredLanguages: readonly string[]): UserLocaleCode {
   for (const preferredLang of preferredLanguages) {
      // Try exact match first (e.g. "de-DE" matches "de-DE")
      const exactMatch = country.locales.find(
         locale => locale.toLowerCase() === preferredLang.toLowerCase()
      );
      if (exactMatch) return exactMatch;

      // Try language-only match (e.g. "de" matches "de-DE")
      const langPrefix = preferredLang.split('-')[0].toLowerCase();
      const prefixMatch = country.locales.find(
         locale => parseUserLocale(locale).language.toLowerCase() === langPrefix
      );
      if (prefixMatch) return prefixMatch;
   }

   return country.locales[0];
}

function browserLanguages(): readonly string[] {
   return typeof navigator === 'undefined' ? [] : navigator.languages;
}

function normalizeCountryCode(countryCode: string | null | undefined): string | null {
   if (countryCode == null) {
      return null;
   }

   const normalized = countryCode.trim().toUpperCase();
   return normalized === '' ? null : normalized;
}
